/**
 * Session-start authorization (sw-ev-api P4b; plan v3.1 §9, §21 P4).
 *
 * The API evaluates every start (create, attach vehicle, PIN start) and,
 * under AUTHZ_MODE=enforce, can refuse it with
 *   403 { error: 'AUTHORIZATION_DENIED', reasonCode, authorization }
 * or allow it on a different footing than the app assumed (a fleet vehicle
 * falling back to card: authorization.fallbackFrom). The server tariff is
 * authoritative under enforce: the rate columns returned on the created
 * session are what the driver will be charged, not the snapshot this app
 * sent (which is still sent, for older API builds).
 *
 * Driver-facing copy never names a client or fleet.
 */

import { displayForBillingMode } from './billingMode';

export const AUTHORIZATION_DENIED = 'AUTHORIZATION_DENIED';
export const PIN_VERIFY_RATE_LIMITED = 'PIN_VERIFY_RATE_LIMITED';

const ACCOUNT_UNAVAILABLE =
  "Account billing isn't available at this charger. You can still charge by paying with a card.";

/**
 * reasonCode → { message, offerCard }. offerCard: the driver can still
 * charge here by paying with a card (the app's existing card flow).
 *
 * FALLBACK ONLY (platform cleanup, Phase 3). A refusal that carries
 * `accessOutcome` (greencharge.access-outcome.v1) is shown on the
 * FLEET_ACCESS_RESULT step with the backend's own title, message and
 * actions, and this map is not consulted for it. The map remains for
 * responses with no `accessOutcome` (an older API, or a refusal by the
 * session-start authorizer rather than the charger's access settings).
 * Do not add codes here for the access outcome; the app does not
 * interpret those.
 */
const DENIAL_COPY = Object.freeze({
  IDENTITY_TOO_WEAK: {
    message: 'Please verify your vehicle PIN to charge on your account.',
    offerCard: true,
  },
  HOST_UNCONFIRMED: { message: ACCOUNT_UNAVAILABLE, offerCard: true },
  CROSS_CLIENT_NOT_ENABLED: { message: ACCOUNT_UNAVAILABLE, offerCard: true },
  CROSS_CLIENT_NO_GRANT: { message: ACCOUNT_UNAVAILABLE, offerCard: true },
  AGREEMENT_MISSING: { message: ACCOUNT_UNAVAILABLE, offerCard: true },
  FLEET_REQUIRED: { message: ACCOUNT_UNAVAILABLE, offerCard: true },
  FLEET_INACTIVE: { message: ACCOUNT_UNAVAILABLE, offerCard: true },
  CLIENT_INACTIVE: { message: ACCOUNT_UNAVAILABLE, offerCard: true },
  FUNDING_INTENT_INVALID: { message: ACCOUNT_UNAVAILABLE, offerCard: true },
  FLEET_RESTRICTION_EXCLUDES_STATION: {
    message: "Your fleet doesn't allow charging at this charger. Please use another charger.",
    offerCard: false,
  },
  HOST_DENY_RULE: {
    message: "This vehicle can't charge at this charger. Please use another charger.",
    offerCard: false,
  },
  GRANT_OUTSIDE_SCHEDULE: {
    message: "This vehicle can't charge at this charger right now. Please try again later or use another charger.",
    offerCard: false,
  },
  PIN_AMBIGUOUS: {
    message: "We couldn't match that PIN to a single vehicle. Please enter your plate and PIN in the app.",
    offerCard: true,
  },
  PIN_INVALID: {
    message: 'The vehicle PIN entered is incorrect.',
    offerCard: true,
  },
  UNKNOWN_VEHICLE: {
    message: "We couldn't find an active vehicle for that plate. You can still charge by paying with a card.",
    offerCard: true,
  },
  VEHICLE_INACTIVE: {
    message: "This vehicle isn't active for account charging. You can still charge by paying with a card.",
    offerCard: true,
  },
  PLATE_AMBIGUOUS: {
    message: "We couldn't match that plate to a single vehicle. You can still charge by paying with a card.",
    offerCard: true,
  },
  STATION_UNAVAILABLE: {
    message: "This charger isn't available right now. Please use another charger.",
    offerCard: false,
  },
  PRIVATE_CHARGER: {
    message: "This charger isn't available for public charging. Please use another charger.",
    offerCard: false,
  },
});

const DENIAL_FALLBACK = Object.freeze({
  message: "We couldn't authorize charging on your account here. You can still charge by paying with a card.",
  offerCard: true,
});

export function authorizationDeniedCopy(reasonCode) {
  return DENIAL_COPY[reasonCode] || DENIAL_FALLBACK;
}

export function authorizationDeniedMessage(reasonCode) {
  return authorizationDeniedCopy(reasonCode).message;
}

export function isAuthorizationDenied(result) {
  return result?.error === AUTHORIZATION_DENIED;
}

/**
 * The applied authorization decides billing only under enforce. Under
 * shadow the legacy outcome is applied and the authorization block is an
 * observation — the app keeps today's behaviour.
 */
export function isEnforcedAuthorization(authorization) {
  return authorization?.mode === 'enforce' && authorization?.applied === true;
}

/** Card fallback actually applied to the session (enforce only). */
export function appliedCardFallback(authorization) {
  if (!isEnforcedAuthorization(authorization)) return null;
  return authorization.fallbackFrom || null;
}

export const CARD_FALLBACK_MESSAGE =
  "Account billing isn't available at this charger, so this session will be paid by card.";

/**
 * Billing rules the server applied, or null to keep the locally resolved
 * ones (no authorization / shadow / older API).
 */
export function enforcedBillingRules(authorization) {
  if (!isEnforcedAuthorization(authorization) || !authorization.billingMode) return null;
  const display = displayForBillingMode(authorization.billingMode);
  return {
    billingMode: authorization.billingMode,
    paymentRequired: display.paymentRequired,
  };
}

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * The pricing snapshot to DISPLAY after create. The server's rate columns
 * on the created session win whenever the authorizer ran
 * (authorization.tariffSummary present) or they differ from what the app
 * sent; otherwise (older API, no columns) the local snapshot is kept.
 */
export function effectivePricingSnapshot(session, localSnapshot) {
  const local = localSnapshot || {};
  if (!session || typeof session !== 'object') return localSnapshot;
  const hasColumns = session.rate_per_kwh !== undefined || session.billing_unit !== undefined;
  if (!hasColumns) return localSnapshot;
  const server = {
    rate_per_kwh: num(session.rate_per_kwh) ?? 0,
    rate_per_minute: num(session.rate_per_minute),
    billing_unit: session.billing_unit || local.billing_unit || 'kwh',
    session_fee: num(session.session_fee) ?? 0,
    currency: session.currency || local.currency || 'BBD',
    min_charge_amount: num(session.min_charge_amount) ?? 0,
  };
  const differs =
    server.billing_unit !== (local.billing_unit || 'kwh')
    || server.rate_per_kwh !== (num(local.rate_per_kwh) ?? 0)
    || (server.rate_per_minute ?? 0) !== (num(local.rate_per_minute) ?? 0)
    || server.session_fee !== (num(local.session_fee) ?? 0)
    || server.min_charge_amount !== (num(local.min_charge_amount) ?? 0)
    || server.currency !== (local.currency || server.currency);
  if (!session.authorization?.tariffSummary && !differs) return localSnapshot;
  return { ...server, source: 'server' };
}

/**
 * The pricing snapshot to DISPLAY from an enforced authorization's
 * tariffSummary. Used after the PIN step attaches the vehicle to a session
 * that was created (as a card session) on the plate screen: the server
 * re-authorizes, the session's tariff becomes the agreement tariff, and the
 * snapshot captured at create time is stale (xcb-e2e). Returns null when
 * there is nothing authoritative to show.
 */
export function pricingSnapshotFromAuthorization(authorization) {
  if (!isEnforcedAuthorization(authorization)) return null;
  const t = authorization.tariffSummary;
  if (!t || typeof t !== 'object') return null;
  const minor = (v) => (num(v) === null ? null : num(v) / 100);
  const legacyShape = t.sessionFeeMinor === undefined && t.sessionFee !== undefined;
  return {
    rate_per_kwh: num(t.energyRate) ?? 0,
    rate_per_minute: num(t.timeRate),
    billing_unit: t.billingUnit || 'kwh',
    session_fee: (legacyShape ? num(t.sessionFee) : minor(t.sessionFeeMinor)) ?? 0,
    min_charge_amount: (legacyShape ? num(t.minimumCharge) : minor(t.minimumChargeMinor)) ?? 0,
    currency: t.currency || 'BBD',
    source: 'server',
  };
}

/** "Try again in 4 minutes" copy for a 429 from /vehicles/verify. */
export function pinRateLimitedMessage(retryAfterSeconds) {
  const s = Number(retryAfterSeconds);
  if (!Number.isFinite(s) || s <= 0) {
    return 'Too many PIN attempts. Please wait a few minutes before trying again. You can still charge by paying with a card.';
  }
  const minutes = Math.ceil(s / 60);
  const when = s < 60
    ? `${Math.ceil(s)} second${Math.ceil(s) === 1 ? '' : 's'}`
    : `${minutes} minute${minutes === 1 ? '' : 's'}`;
  return `Too many PIN attempts. Please try again in ${when}. You can still charge by paying with a card.`;
}
